/**
 * CombatResultStamp — rubber-stamp result indicator over the answer.
 *
 * Two presentations:
 *  - <HitStamp />   chalk checkmark in success-green, scribble-on draws
 *  - <MissStamp />  chalk X in vermillion, two crossed strokes draw in turn
 *
 * The drawing animation uses Motion's `pathLength` from 0 → 1 with an
 * eased curve so the strokes look like a hand committing to the answer
 * rather than a vector reveal. Total duration ≈ 400ms.
 *
 * Honours `prefers-reduced-motion`: skips the drawing animation and
 * presents the final stamp shape statically.
 */

import { motion } from 'motion/react';
import { surfaces } from '@qd/design-tokens';
import { FILTER_IDS } from '../../filters';
import { useReducedMotion } from '../../hooks/useReducedMotion';

type StampVariant = 'hit' | 'miss';

interface CombatResultStampProps {
  variant: StampVariant;
  /** Pixel size (square). Default 180px. */
  size?: number;
  /** Override default colour (chalk-green for hit, vermillion for miss). */
  color?: string;
  /** Pause before strokes begin to draw, in seconds. */
  delay?: number;
}

// "Chalk-green" — derived hue for HIT result. Keeps the slate/paper system
// untouched while giving the success state a distinct semantic colour.
// Picked to read on cream paper without competing with the vermillion CTA.
const HIT_GREEN = '#3F7A6E'; // matches verdigris from the player palette

export function CombatResultStamp({
  variant,
  size = 180,
  color,
  delay = 0,
}: CombatResultStampProps) {
  const reduced = useReducedMotion();
  const stroke = color ?? (variant === 'hit' ? HIT_GREEN : surfaces.waxRed);

  // Drawing uses Motion's pathLength control on stroke-dasharray. We hide
  // the path until the animation begins so there's no vector flash.
  const strokeDuration = 0.36;
  const transition = reduced
    ? { duration: 0 }
    : {
        duration: strokeDuration,
        delay,
        ease: [0.65, 0, 0.35, 1] as [number, number, number, number],
      };

  return (
    <motion.svg
      role="img"
      aria-label={variant === 'hit' ? 'Test passed' : 'Defect found'}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={{
        // Slight rotation for that off-axis "stamped" quality.
        transform: `rotate(${variant === 'hit' ? -6 : 4}deg)`,
        filter: `url(#${FILTER_IDS.chalkRoughen})`,
      }}
      initial={reduced ? false : { scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={reduced ? { duration: 0 } : { duration: 0.18, delay }}
    >
      {variant === 'hit' ? (
        <CheckPath stroke={stroke} transition={transition} reduced={reduced} />
      ) : (
        <XPaths stroke={stroke} transition={transition} reduced={reduced} delay={delay} />
      )}
    </motion.svg>
  );
}

interface PathProps {
  stroke: string;
  transition: { duration: number; delay?: number; ease?: [number, number, number, number] };
  reduced: boolean;
  delay?: number;
}

function CheckPath({ stroke, transition, reduced }: PathProps) {
  // Two-segment checkmark: short downstroke into the heel, long upstroke
  // into the tick. Drawn as one path so pathLength reads naturally.
  const d = 'M 18 52 L 42 76 L 84 28';
  return (
    <motion.path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth={9}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={0.92}
      initial={reduced ? false : { pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={transition}
    />
  );
}

function XPaths({ stroke, transition, reduced, delay = 0 }: PathProps) {
  // Two crossed strokes drawn in sequence so the X reads as deliberate
  // hand motion rather than a single splat. Second stroke starts halfway
  // through the first.
  const second = {
    ...transition,
    delay: delay + transition.duration * 0.5,
  };
  return (
    <>
      <motion.path
        d="M 22 22 L 78 78"
        fill="none"
        stroke={stroke}
        strokeWidth={9}
        strokeLinecap="round"
        opacity={0.92}
        initial={reduced ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={transition}
      />
      <motion.path
        d="M 78 22 L 22 78"
        fill="none"
        stroke={stroke}
        strokeWidth={9}
        strokeLinecap="round"
        opacity={0.92}
        initial={reduced ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={second}
      />
    </>
  );
}

/** Ergonomic wrappers so callers can write `<HitStamp />` directly. */
export function HitStamp(props: Omit<CombatResultStampProps, 'variant'>) {
  return <CombatResultStamp variant="hit" {...props} />;
}

export function MissStamp(props: Omit<CombatResultStampProps, 'variant'>) {
  return <CombatResultStamp variant="miss" {...props} />;
}
